/**
 * Battle-scherm: baas-paneel (HP/Verbinding + getelegrafeerde aanval), speler-
 * paneel (HP/energie/schild/statussen/aura), hand, verloop-log en win/verlies-
 * overlay. Per-chakra getint via de header-kleuren.
 */
import { useMemo } from 'react';
import type { Aanval } from '../data/types';
import { HEADERS } from '../data/headers';
import { useBattleStore } from '../store/battleStore';
import { effectieveKost, speelbaarReden } from '../engine/battle';
import { KaartView } from './KaartView';
import { BaasBlob } from './art/BaasBlob';
import { baasTitel } from '../data/content';
import { t } from '../i18n';

function intentSamenvatting(a: Aanval): string {
  const e = a.effect;
  const d: string[] = [];
  if (e.dmg) d.push(`${e.dmg} schade`);
  if (e.conditionalDmg) d.push(`${e.conditionalDmg.amt} voorw. schade`);
  if (e.energyLoss) d.push(`−${e.energyLoss} energie`);
  if (e.verbinding) d.push(`${e.verbinding} Verbinding`);
  if (e.discard) d.push(`legt ${e.discard.n} af`);
  if (e.freeze || e.freezeOne) d.push('bevriest');
  if (e.shuffleHand) d.push('schudt hand');
  if (e.status) d.push('debuff');
  return d.join(', ') || a.naam;
}

const STATUS_LABEL: Record<string, string> = {
  costUp: 'duurder', lockType: 'type vergrendeld', lockOrigin: 'origin vergrendeld', onlyType: 'alleen 1 type',
  drawLess: 'minder trekken', energyDrainPerTurn: 'energielek', nullifyType: 'type uit', nullifyAll: 'alles uit',
  nullifyHeal: 'geen heling', noBossDmg: 'geen baas-schade', halfEffect: 'half effect', halfEffectType: 'half (type)',
  debuffOutput: 'output −', skipTurn: 'beurt over', veil: 'sluier', reverseNext: 'omgekeerd', randomize: 'willekeurig',
  drawFromDiscard: 'uit afleg', maxPlay: 'max kaarten', selfDamageShare: 'echo', energySteal: 'energieroof', comboDisabled: 'geen combo',
};

interface BattleScreenProps {
  /** Als gezet: bij einde gevecht wordt dit aangeroepen i.p.v. de losse overlay
   * (de scène-flow toont dan het leermoment). */
  onVoltooid?: (resultaat: 'gewonnen' | 'verloren') => void;
}

export function BattleScreen({ onVoltooid }: BattleScreenProps = {}) {
  const { battle, taal, speel, eindBeurt, herstart, stop } = useBattleStore();

  const header = battle ? HEADERS[battle.chakra] : null;
  const intentTekst = useMemo(() => (battle?.baasState.intent ? intentSamenvatting(battle.baasState.intent) : null), [battle]);

  if (!battle || !header) return null;
  const { speler, baasState, hand, deck, discard, log, resultaat, bevroren, beurt } = battle;
  const baas = baasState.baas;
  const kleur = header.kleur;
  const accent = header.accent;

  const hpPct = baas.verbindingFight ? baasState.verbinding : baas.hp ? Math.round((baasState.hp / baas.hp) * 100) : 0;

  return (
    <div className="relative flex h-full w-full flex-col" style={{ ['--chakra' as string]: kleur }}>
      {/* BAAS */}
      <div className="relative flex items-center gap-4 border-b border-white/10 p-3" style={{ background: `linear-gradient(180deg, ${kleur}22, transparent)` }}>
        {/* gezichtsloze, morphende baas-presence */}
        <BaasBlob kleur={kleur} accent={accent} grootte={80} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold" style={{ fontFamily: 'Cinzel, serif', color: '#ece6da' }}>{baas.naam}</span>
            <span className="text-xs text-slate-400">— {baasTitel(baas, taal)}</span>
            <span className="ml-auto rounded px-2 py-0.5 text-[10px] uppercase tracking-wide" style={{ background: `${accent}33`, color: accent }}>
              {baas.eindbaas ? t('ui.eindbaas', taal) : t('ui.subbaas', taal)}
            </span>
          </div>
          {/* HP / Verbinding */}
          <div className="mt-1 h-3 overflow-hidden rounded-full bg-black/40">
            <div className="h-full rounded-full transition-all" style={{ width: `${hpPct}%`, background: baas.verbindingFight ? accent : '#c0392b' }} />
          </div>
          <div className="mt-0.5 flex items-center justify-between text-[11px] text-slate-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            <span>{baas.verbindingFight ? `${t('ui.verbinding', taal)} ${baasState.verbinding}/100` : `HP ${baasState.hp}/${baas.hp}`}</span>
            <span>⚡{baasState.energie}{baasState.stacks > 0 ? ` · stacks ${baasState.stacks}/3` : ''}</span>
          </div>
          {/* intent (telegraferen) */}
          <div className="mt-1 text-[12px]">
            <span className="text-slate-400">{t('ui.intent', taal)}: </span>
            {battle.baasState.intent ? (
              <span style={{ color: accent }}>⚔ {battle.baasState.intent.naam} <span className="text-slate-400">({intentTekst})</span></span>
            ) : (
              <span className="text-slate-500">{t('ui.geenIntent', taal)}</span>
            )}
          </div>
        </div>
      </div>

      {/* MIDDEN: log + speler-statussen */}
      <div className="flex min-h-0 flex-1">
        <div className="flex w-44 shrink-0 flex-col border-r border-white/10 p-2">
          <div className="mb-1 text-[10px] uppercase tracking-widest text-slate-500">{t('ui.log', taal)}</div>
          <div className="flex flex-1 flex-col-reverse gap-0.5 overflow-y-auto text-[11px] leading-snug text-slate-300">
            {log.slice(-40).reverse().map((r, i) => (<div key={log.length - i}>{r}</div>))}
          </div>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-2">
          {/* speler-statussen */}
          <div className="flex flex-wrap justify-center gap-1">
            {speler.aura && (
              <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] text-emerald-200">◎ {speler.aura.name}</span>
            )}
            {speler.statuses.map((s, i) => (
              <span key={i} className="rounded-full border border-rose-400/40 bg-rose-400/10 px-2 py-0.5 text-[10px] text-rose-200">
                {STATUS_LABEL[s.kind] ?? s.kind}{s.resterend > 1 ? ` ${s.resterend}` : ''}
              </span>
            ))}
            {speler.reflectVolgende && <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] text-amber-200">↩ reflect</span>}
          </div>
        </div>
      </div>

      {/* SPELER-balk */}
      <div className="flex items-center gap-4 border-t border-white/10 px-3 py-2 text-[12px]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        <span className="text-rose-300">♥ {speler.hp}/{speler.maxHp}</span>
        {speler.schild > 0 && <span className="text-sky-300">⛉ {speler.schild}</span>}
        <span className="flex items-center gap-1 text-amber-300">
          {Array.from({ length: speler.maxEnergie }).map((_, i) => (
            <span key={i} style={{ opacity: i < speler.energie ? 1 : 0.25 }}>⬢</span>
          ))}
          <span className="ml-1 text-slate-400">{speler.energie}/{speler.maxEnergie}</span>
        </span>
        <span className="ml-auto text-slate-400">{t('ui.beurt', taal, { n: beurt })} · {t('ui.deck', taal)} {deck.length} · {t('ui.afleg', taal)} {discard.length}</span>
        <button
          type="button"
          onClick={eindBeurt}
          disabled={resultaat !== 'bezig'}
          className="rounded-lg px-4 py-1.5 text-sm font-semibold text-black transition hover:brightness-110 disabled:opacity-40"
          style={{ background: accent }}
        >
          {t('ui.eindigBeurt', taal)}
        </button>
      </div>

      {/* HAND */}
      <div className="flex items-end gap-2 overflow-x-auto border-t border-white/10 bg-black/30 p-3" style={{ minHeight: '11rem' }}>
        {hand.map((inst) => (
          <KaartView
            key={inst.instId}
            inst={inst}
            kleur={kleur}
            accent={accent}
            taal={taal}
            bevroren={bevroren.includes(inst.instId)}
            speelbaar={resultaat === 'bezig' && speelbaarReden(battle, inst) === null}
            reden={speelbaarReden(battle, inst)}
            onSpeel={speel}
          />
        ))}
        {hand.length === 0 && <div className="p-6 text-sm text-slate-500">—</div>}
        <div className="ml-2 self-center text-[10px] text-slate-600">
          {hand.map((i) => effectieveKost(battle, i)).length ? '' : ''}
        </div>
      </div>

      {/* OVERLAY win/verlies */}
      {resultaat !== 'bezig' && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-black/80 backdrop-blur-sm">
          <div className="text-3xl font-bold" style={{ fontFamily: 'Cinzel, serif', color: resultaat === 'gewonnen' ? accent : '#c0392b' }}>
            {resultaat === 'gewonnen' ? t('ui.gewonnen', taal) : t('ui.verloren', taal)}
          </div>
          {onVoltooid ? (
            // scène-flow: door naar leermoment (winst) of opnieuw/verlaten (verlies)
            resultaat === 'gewonnen' ? (
              <button type="button" onClick={() => onVoltooid('gewonnen')} className="rounded-lg px-6 py-2 text-sm font-semibold text-black" style={{ background: accent }}>{t('vn.verder', taal)} →</button>
            ) : (
              <div className="flex gap-3">
                <button type="button" onClick={herstart} className="rounded-lg px-4 py-2 text-sm font-semibold text-black" style={{ background: accent }}>{t('ui.opnieuw', taal)}</button>
                <button type="button" onClick={() => onVoltooid('verloren')} className="rounded-lg border border-white/20 px-4 py-2 text-sm text-slate-200">↩</button>
              </div>
            )
          ) : (
            <>
              {resultaat === 'gewonnen' && baas.leermoment && (
                <p className="max-w-xl px-6 text-center text-sm leading-relaxed text-slate-300" style={{ fontFamily: 'EB Garamond, serif' }}>{baas.leermoment}</p>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={herstart} className="rounded-lg px-4 py-2 text-sm font-semibold text-black" style={{ background: accent }}>{t('ui.opnieuw', taal)}</button>
                <button type="button" onClick={stop} className="rounded-lg border border-white/20 px-4 py-2 text-sm text-slate-200">↩</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
